/**
 * Feb 2026 R293za · ReceivablesTiles — four at-a-glance tiles that
 * sit at the top of ClientDetail (and anywhere else a per-customer
 * money snapshot is useful). Pulls from
 *   GET /gst/workspaces/{ws}/receivables?customer_name=X
 * which rolls up every live invoice + accepted quotation.
 *
 * Why the tiles are in this file:
 *  - Shared by ClientDetail (big header) and the Quotation / Invoice
 *    detail dialog (compact mode) so a user never has to flip pages
 *    to see how much a customer owes before extending them more work.
 *
 * Tiles:
 *   Billed       — Σ invoice.grand_total (non-void)
 *   Received     — Σ payments against those invoices
 *   Outstanding  — billed − received − tds (clamped ≥ 0)
 *   Overdue      — subset of Outstanding where due_date passed
 *   Quoted (sent)+ Quoted (accepted) — pipeline chips below the row
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtMoney } from "@/lib/currency";
import { Receipt, Wallet, Clock, AlertTriangle, FileText, CheckCircle2 } from "lucide-react";

function Tile({ icon: Icon, label, amount, tone = "neutral", testid, hint, currency = "INR" }) {
  const toneCls = {
    neutral: "bg-card border-border",
    good:    "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:border-emerald-800",
    warn:    "bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800",
    bad:     "bg-rose-50 border-rose-200 dark:bg-rose-950/40 dark:border-rose-800",
  }[tone] || "bg-card border-border";
  const iconCls = {
    neutral: "text-muted-foreground",
    good:    "text-emerald-700 dark:text-emerald-200",
    warn:    "text-amber-700 dark:text-amber-200",
    bad:     "text-rose-700 dark:text-rose-200",
  }[tone] || "text-muted-foreground";
  return (
    <div
      className={`border rounded-lg p-3 ${toneCls} min-w-0`}
      data-testid={testid}
      title={hint}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold">
        <Icon className={`h-3 w-3 ${iconCls}`} />
        <span className={iconCls}>{label}</span>
      </div>
      <div className="font-bold text-base sm:text-lg mt-1 truncate" data-testid={`${testid}-amount`}>
        {fmtMoney(amount || 0, currency)}
      </div>
    </div>
  );
}

export default function ReceivablesTiles({ ws, customerName, compact = false, currency }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!ws || !customerName) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ customer_name: customerName });
        if (currency) params.set("currency", currency);
        const r = await api.get(
          `/gst/workspaces/${ws}/receivables?${params.toString()}`);
        if (!cancelled) setData(r.data);
      } catch (e) { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [ws, customerName, currency]);

  if (loading && !data) {
    return (
      <div className="h-20 border border-dashed border-border rounded-lg flex items-center justify-center text-xs text-muted-foreground"
            data-testid="client-receivables-loading">
        Loading receivables…
      </div>
    );
  }
  if (!data || (data.invoiced === 0 && data.quoted_sent === 0 && data.quoted_accepted === 0)) {
    return (
      <div className="border border-dashed border-border rounded-lg p-3 text-xs text-muted-foreground"
            data-testid="client-receivables-empty">
        No invoices or quotations yet for <b>{customerName}</b>.
      </div>
    );
  }

  // Feb 2026 R293zze · Use the backend-reported currency (which honors
  // the filter if we passed one) so the tile and the chips always
  // match what the user is looking at.
  const cur = data.currency || currency || "INR";

  return (
    <div data-testid="client-receivables-tiles">
      <div className={`grid gap-2 ${compact ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-2 lg:grid-cols-4"}`}>
        <Tile
          icon={Receipt}
          label="Billed"
          amount={data.invoiced}
          currency={cur}
          tone="neutral"
          testid="recv-billed"
          hint="Sum of every live invoice's grand total" />
        <Tile
          icon={Wallet}
          label="Received"
          amount={data.received}
          currency={cur}
          tone="good"
          testid="recv-received"
          hint="Payments recorded against those invoices" />
        <Tile
          icon={Clock}
          label="Outstanding"
          amount={data.outstanding}
          currency={cur}
          tone={data.outstanding > 0 ? "warn" : "neutral"}
          testid="recv-outstanding"
          hint="Billed − received − TDS (clamped at zero)" />
        <Tile
          icon={AlertTriangle}
          label="Overdue"
          amount={data.overdue}
          currency={cur}
          tone={data.overdue > 0 ? "bad" : "neutral"}
          testid="recv-overdue"
          hint="Portion of Outstanding where the due date has passed" />
      </div>
      {(data.quoted_sent > 0 || data.quoted_accepted > 0) && (
        <div className="flex flex-wrap gap-2 mt-2">
          {data.quoted_accepted > 0 && (
            <div className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border border-emerald-300 dark:border-emerald-800 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200"
                 data-testid="recv-quoted-accepted"
                 title="Accepted quotations not yet converted to a Sales Order">
              <CheckCircle2 className="h-3 w-3" />
              Accepted quotations: <b>{fmtMoney(data.quoted_accepted, cur)}</b>
            </div>
          )}
          {data.quoted_sent > 0 && (
            <div className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 border border-blue-300 dark:border-blue-800 rounded bg-blue-50 dark:bg-blue-950/40 text-blue-800 dark:text-blue-200"
                 data-testid="recv-quoted-sent"
                 title="Quotations sent to the client, awaiting response">
              <FileText className="h-3 w-3" />
              Pending quotations: <b>{fmtMoney(data.quoted_sent, cur)}</b>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
